import * as React from 'react';
import './UppyWrapper.scss';
import { FileUploader, UploaderState } from './FileUploader';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';
import { Notify } from '@shared/ui-native/Notify';
import { S3PresignResult } from '@shared/files/FileServiceClient';

import Uppy, { UploadedUppyFile, UppyFile } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Webcam from '@uppy/webcam';
import AwsS3 from '@uppy/aws-s3';
import StatusBar from '@uppy/status-bar';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';
import uppy_en from '@uppy/locales/lib/en_US';
import uppy_de from '@uppy/locales/lib/de_DE';
import uppy_dz from '@uppy/locales/lib/cs_CZ';
import uppy_ru from '@uppy/locales/lib/ru_RU';
import uppy_fi from '@uppy/locales/lib/fi_FI';
import uppy_es from '@uppy/locales/lib/es_ES';
import uppy_hu from '@uppy/locales/lib/hu_HU';
import uppy_nl from '@uppy/locales/lib/nl_NL';
import uppy_pt from '@uppy/locales/lib/pt_PT';
import uppy_sv from '@uppy/locales/lib/sv_SE';
import uppy_ja from '@uppy/locales/lib/ja_JP';
import uppy_ko from '@uppy/locales/lib/ko_KR';
import uppy_uk from '@uppy/locales/lib/uk_UA';
import uppy_no from '@uppy/locales/lib/nb_NO';
import uppy_fr from '@uppy/locales/lib/fr_FR';
import uppy_he from '@uppy/locales/lib/he_IL';
import uppy_rs from '@uppy/locales/lib/sr_RS_Latin';
import { Message } from 'primereact/message';


@observer
export class UppyWrapper extends React.Component<
    {
        onlyImages?: boolean;
        capture?: boolean;
        onAddedFile?: (file?: UppyFile, uppyInstance?: Uppy) => Promise<void>;
        onQueueComplete?: (successful?: UploadedUppyFile<any, any>[]) => void;
        isFileNewVersion?: (file: File) => boolean;
        onClickDone?: () => void;
        initialFiles?: File[];
        getS3Url: (file: UppyFile) => Promise<S3PresignResult>;
        postProcess: (files: UppyFile[]) => Promise<any>;
        cantUploadMsg?: string;
        disabled?: boolean;
        disallowedExtensions?: string[];
        disallowedExtensionsMsg?: string;
        children?: React.ReactNode;
        getPhotoMetadata?: (file: UppyFile) => Record<string, string>;
    },
    {}
> {
    static defaultProps = {
        onlyImages: false,
    };

    @observable uploaderState: UploaderState = UploaderState.INIT;
    private uppyInstance: Uppy;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    async componentDidMount() {
        const localeToLanguagePack = {
            en: uppy_en,
            de: uppy_de,
            dz: uppy_dz,
            ru: uppy_ru,
            fi: uppy_fi,
            es: uppy_es,
            hu: uppy_hu,
            nl: uppy_nl,
            pt: uppy_pt,
            sv: uppy_sv,
            ja: uppy_ja,
            ko: uppy_ko,
            uk: uppy_uk,
            no: uppy_no,
            he: uppy_he,
            rs: uppy_rs,
            fr: uppy_fr,
        };
        const uppyInstance = new Uppy({
            locale: localeToLanguagePack[kuura.getLanguage()] ?? localeToLanguagePack.en,
            autoProceed: false,
            restrictions: {
                maxFileSize: parseInt(String(kuura.getMaxUploadFileSizeBytes())),
                allowedFileTypes: this.props.onlyImages ? ['image/*'] : null,
                maxNumberOfFiles: 100,
            },
            onBeforeFileAdded: file => {
                if (Array.isArray(this.props.disallowedExtensions) && this.props.disallowedExtensions.length > 0) {
                    const ext = file.extension;
                    if (this.props.disallowedExtensions.includes(ext)) {
                        uppyInstance.info(this.props.disallowedExtensionsMsg ?? 'File type forbidden', 'error');
                        return false;
                    }
                }
                return file;
            },
        })
            .use(StatusBar, {
                target: '#uppyStatusBar',
                hideCancelButton: true,
                hideRetryButton: true,
                hidePauseResumeButton: true,
                hideUploadButton: true,
                showProgressDetails: true,
                hideAfterFinish: false,
            })
            .use(Dashboard, {
                inline: true,
                target: '#uppyWrapper',
                showProgressDetails: true,
                proudlyDisplayPoweredByUppy: false,
                singleFileFullScreen: false,
                hideUploadButton: true,
                hidePauseResumeButton: true,
                hideProgressAfterFinish: false,
                hideRetryButton: true,
                height: '100%',
                width: 550,
                hideCancelButton: true,
                disableStatusBar: true,
                showRemoveButtonAfterComplete: false,
                disableInformer: false,
                metaFields: file => {
                    const deflateField = {
                        id: 'deflate',
                        name: __('js_files.unzip'),
                        render({ value, onChange, required, form }, h) {
                            return h('input', {
                                type: 'checkbox',
                                required,
                                form,
                                onChange: ev => onChange(ev.target.checked),
                                defaultChecked: true,
                            });
                        },
                    };

                    const metadataFields = window.isShiftKeyDown
                        ? {
                              id: 'photo-metadata',
                              name: __('js_files.photo_metadata'),
                              render: ({ value, onChange, required, form }, h) => {
                                  const metadata = this.props.getPhotoMetadata(file);
                                  if (!metadata) {
                                      return h('div', {}, '/');
                                  }
                                  return h(
                                      'div',
                                      {
                                          style: 'font-size: 12px; line-height: 1.3em;',
                                      },
                                      Object.entries(metadata).map(([key, value]) => h('div', {}, `${key}: ${value}`))
                                  );
                              },
                          }
                        : null;

                    return [
                        { id: 'description', name: __('general.description') },
                        !file.type.startsWith('image/') ? { id: 'comment', name: __('js_files.email_comment') } : null,
                        file.type.startsWith('image/') && this.props.getPhotoMetadata ? metadataFields : null,
                        utils.getExtensionFromFilename(file.name) === 'zip' ? deflateField : null,
                    ].filter(Boolean);
                },
            })
            .use(Webcam, { target: Dashboard })
            .use(AwsS3, {
                shouldUseMultipart: false,
                getUploadParameters: async file => {
                    try {
                        const s3presignResult = await this.props.getS3Url(file);
                        console.log('UppyWrapper getUploadParameters s3presignResult:', s3presignResult);
                        if (!s3presignResult) {
                            console.error('Upload error - S3 presign failed');
                            throw new Error(__('js_files.msg.error.unknown_upload_error'));
                        }
                        (file as any).infrakitUuid = s3presignResult.document?.uuid;

                        return {
                            url: s3presignResult.uploadUrl,
                            method: 'PUT',
                            fields: {},
                            headers: {
                                'Content-Type': s3presignResult.contentType,
                                'Content-MD5': s3presignResult.checksumMd5b64,
                            },
                        };
                    } catch (e) {
                        console.error('UppyWrapper getUploadParameters error:', e);

                        if (e?.response?.status === 409) {
                            FileUploader.printUppyStatusForFile(file, kuura.getMessage('js_files.msg.error.validation_identical_file'), 'red');
                            throw new Error(__('js_files.msg.error.validation_identical_file'));
                        } else if (e?.response?.status >= 400) {
                            throw new Error(__('js_files.msg.error.unknown_upload_error'));
                        } else if (e?.message.includes('timeout')) {
                            throw new Error(__('js_files.msg.error.upload_timeout'));
                        } else {
                            throw new Error(__('js_files.msg.error.unknown_upload_error'));
                        }
                    }
                },
            })
            .on('complete', data => {
                // all completed
                this.uploaderState = UploaderState.DONE;
                this.props.onQueueComplete(data.successful);
                Notify.success(__('tablet.photo_upload_complete'));
            })
            .on('files-added', files => {
                files.forEach(onFileAdded);
                addScrollListener();
            })
            .on('file-removed', files => {
                this.uploaderState = uppyInstance.getFiles().length > 0 ? UploaderState.FILES_ADDED : UploaderState.EMPTY;
            })
            .on('preprocess-progress' as any, data => {
                this.uploaderState = UploaderState.UPLOADING;
            })
            .on('upload', data => {
                this.uploaderState = UploaderState.UPLOADING;
            })
            .on('upload-success', (file, response) => {})
            .on('error', error => {
                // when entire upload fails
                this.uploaderState = UploaderState.DONE;
            });

        uppyInstance.addPostProcessor(async files => {
            if (this.props.postProcess) {
                await this.props.postProcess(files.map(id => this.uppyInstance.getFile(id)));
            }
            // files are processed async, we dont know when to refresh the page, so do an optimistic refresh after 5sec
            await new Promise<void>(done => {
                setTimeout(done, 5000);
            });
            return null;
        });

        const onFileAdded = (file: UppyFile) => {
            this.uploaderState = uppyInstance.getFiles().length > 0 ? UploaderState.FILES_ADDED : UploaderState.EMPTY;
            this.props.onAddedFile(file, uppyInstance);
            setTimeout(() => {
                // must wait for uppy item to render first
                this.renderFileLabels();
            }, 1);
        };

        const addScrollListener = _.debounce(() => {
            // uppy uses virtualized list for scrolling, so it recreating elements during scroll and resetting html changes done in renderFileLabels
            // they offer no api to customize rendering and in github issues they refuse to add one for now, so we need this hack
            const wrapper = document.querySelector('.uppy-Dashboard-files') as HTMLDivElement;
            if (wrapper) {
                wrapper.removeEventListener('scroll', this.renderFileLabels);
                wrapper.addEventListener('scroll', this.renderFileLabels);
            }
        }, 50);

        this.uploaderState = UploaderState.EMPTY;
        this.uppyInstance = uppyInstance;

        // initial files from drag and drop
        if (Array.isArray(this.props.initialFiles)) {
            this.props.initialFiles.forEach(file => uppyInstance.addFile(file as any));
            setTimeout(() => {
                this.uploaderState = uppyInstance.getFiles().length > 0 ? UploaderState.FILES_ADDED : UploaderState.EMPTY;
            });
        }
    }

    appendLabelForFIle = (file: UppyFile, msg: string, badgeClass: string = '') => {
        const el = document.querySelector(`.uppy-Dashboard-Item-name[title="${file.name}"]`).parentNode.querySelector('.uppy-custom-name');
        $(el).append(`&nbsp;<span class="badge badge-sm ${badgeClass}">${msg}</span>`);
    };

    renderFileLabels = _.throttle(() => {
        this.uppyInstance.getFiles().forEach(file => {
            const el = document.querySelector(`.uppy-Dashboard-Item-name[title="${file.name}"]`) as HTMLDivElement;
            if (!el) return;
            el.style.display = 'none';
            // can't modify content of .uppy-Dashboard-Item-name directly because uppy will just append name to it again during upload
            $(el.parentNode).find('.uppy-custom-name').remove();
            $(el.parentNode).prepend(`<div class="uppy-custom-name">${file.name}</div>`);

            if (FileUploader.isModel(file.name)) {
                this.appendLabelForFIle(file, kuura.getMessage('general.model'));
            }
            if (typeof this.props.isFileNewVersion === 'function' && this.props.isFileNewVersion(file.data as File)) {
                this.appendLabelForFIle(file, kuura.getMessage('js_files.new_version', 'badge-success'));
            }
        });
    }, 5);

    componentWillUnmount() {
        // destroy
    }

    render() {
        const isUploading = this.uploaderState === UploaderState.UPLOADING;

        return (
            <div className={cs('flex-column', { 'overlay-loading': this.uploaderState == UploaderState.INIT })} style={{ overflowY: 'hidden' }} id='image-uploader'>
                <div className='flex-column gap-half of-a'>
                    <div id='uppyWrapper' className='card' style={{ height: '33vh', minHeight: 220 }}></div>
                    <div className={cs('flex-column of-h', { 'fade-disable': isUploading })}>{this.props.children}</div>
                </div>

                <div className='flex-row gap-half' style={{ marginTop: 20 }}>
                    {this.props.cantUploadMsg ? (
                        <Message severity='error' text={this.props.cantUploadMsg} style={{ width: '100%' }} />
                    ) : (
                        <>
                            <div
                                id='uppyStatusBar'
                                style={{ flex: 100, display: this.uploaderState == UploaderState.UPLOADING || this.uploaderState == UploaderState.DONE ? 'block' : 'none' }}
                            ></div>
                            <Button
                                style={{ flex: 1 }}
                                className={cs('ik-button-large', { 'ik-button-primary': true })}
                                onClick={this.startUpload}
                                disabled={this.uploaderState != UploaderState.FILES_ADDED && this.uploaderState != UploaderState.DONE}
                            >
                                {FileUploader.btnTextForState[this.uploaderState]}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    startUpload = () => {
        if (this.uploaderState === UploaderState.DONE) {
            if (typeof this.props.onClickDone === 'function') {
                this.props.onClickDone();
            }
        } else {
            this.uppyInstance.upload();
            this.uploaderState = UploaderState.UPLOADING;
        }
    };
}
